/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import EntryAndExitFormDetailsSectionInfo
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/EntryAndExitFormDetailsSectionInfo";
import {permissionObj} from "../../../../../../../Helpers/permission";
import routes from "../../../../../../../Routes/routes";
import DivContainerBody from "../../../../../../../components/DivContainerBody";
import HeadTittle from "../../../../../../../components/HeadTittle";
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {CubeIcon, EditIcon, IconDocumentWarning} from "../../../../../../../components/buttons/IconButtons";
import ButtonAction from "../../../../../../../components/buttons/ButtonAction";
import ReportDispatchModal from "../Modal/ReportDispatchModal";
import StatusBadgeSection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/StatusBadgeSection";
import DiscrepancySection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/DiscrepancySection";
import GuardGateLogSection from "../Sections/GuardGateLogSection";
import {
    getGateLogNotePlaceholder,
    MATERIAL_CONTROL_REGISTERED_GATE_STATUS,
    showsGuardGateMovementLog,
    wasGuardReportedOnVehicleMovement
} from "../Model/Model";
import MaterialDispatchListSection
    from "../../../../../MaterialControl/SubComponents/MaterialRequest/MaterialDispatch/MaterialDispatchListSection";
import {wasMaterialControlDispatched} from "../../../../../MaterialControl/Model/Model";
import GateLogCard
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/GateLogCard";
import MaterialsSection, {MATERIALS_MODE}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/MaterialsSection";

/**
 * Detail of a general entry and exit form read by the guard at the gate.
 *
 * The information of the folio is the one the requester registered, so the
 * guard only reads it and completes the gate log of the movement: the notes of
 * what arrives at the plant and the evidence that supports it. A movement
 * reported with discrepancies displays them above the log, because it has to
 * be reviewed before the materials can be dispatched.
 *
 * @param {Object} props - Information of the folio and handlers of the gate log.
 * @param {Function} props.t - Translation handler.
 * @param {Function} props.navigate - Navigation handler.
 * @param {boolean} props.DEV - Whether the development mode is active.
 * @param {Array} props.permission - Permissions of the user reading the folio.
 * @param {Object} props.contentCardRef - Reference of the content of the information card.
 * @param {Object} props.contentTableRef - Reference of the content of the gate log.
 * @param {Object} props.containerCardRef - Reference of the container of the information card.
 * @param {Object} props.containerTableRef - Reference of the container of the gate log.
 * @param {Object} props.item - Folio the movement belongs to.
 * @param {Array} props.evidence - Evidence registered by the requester.
 * @param {Object} props.form - Type of the form being displayed.
 * @param {boolean} props.showBadge - Whether the dispatch of the folio is displayed.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {Object} props.photoPicker - Evidence of the movement captured at the gate.
 * @param {Function} props.setPhotoPicker - State updater of the evidence of the gate.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {Function} props.setModified - Reads the folio again when a material is delivered.
 * @param {boolean} props.showConfirmModal - Whether the confirmation of the report is displayed.
 * @param {Function} props.handleDispatch - Registers the dispatch of the movement.
 * @param {Function} props.openReportModal - Opens the report of the discrepancies.
 * @param {Function} props.handleActionModal - Registers the confirmed report with its comment and evidence.
 * @param {Function} props.closeModal - Closes the confirmation of the report.
 * @param {Function} props.goBack - Returns to the list of forms.
 *
 * @returns {JSX.Element} Detail of the folio with the gate log of the guard.
 */
const GuardGeneralEntryExitDetails = ({
                                          t,
                                          navigate,
                                          DEV,
                                          isGuard,
                                          permission,
                                          contentCardRef,
                                          contentTableRef,
                                          containerCardRef,
                                          containerTableRef,
                                          item,
                                          evidence,
                                          form,
                                          showBadge,
                                          gateLog,
                                          handleChange,
                                          photoPicker,
                                          setPhotoPicker,
                                          emptyFields,
                                          setModified,
                                          showConfirmModal,
                                          handleDispatch,
                                          openReportModal,
                                          handleActionModal,
                                          closeModal,
                                          goBack
                                      }) => {

    if (!DEV && !isGuard &&
        !permission?.includes(permissionObj.WEB_ENTRY_AND_EXIT_FORMS_READ)
    )
        navigate(routes.forbidden);

    // The dispatch hands the material over and the departure takes it out of
    // the plant, a folio reported with discrepancies is still open, so the
    // actions that close it are read until one of those movements is
    // registered.
    const isClosed = MATERIAL_CONTROL_REGISTERED_GATE_STATUS.includes(item?.dispatchStatusId);

    const hasDiscrepancies = item?.dispatchStatusId === STATUS_MODEL.DISCREPANCY;

    // The materials are validated at the gate until the dispatch is registered,
    // so the folio that already handed them over is the one that only reads the
    // review of every material.
    const isDispatched = wasMaterialControlDispatched(item);

    // The report replaces the status the folio was waiting in, so a folio
    // reported during a movement of the vehicle keeps reading its log, only
    // read, whether the guard already wrote it or was still waiting to.
    const showReportedLog = wasGuardReportedOnVehicleMovement(item);

    // A folio reported while its materials were being validated keeps reading
    // the same list, only read; one reported while the vehicle was awaited never
    // reached it, so the list is not opened on it.
    const showMaterialsToValidate = !isDispatched && !showReportedLog;

    // A folio reported with discrepancies has to be reviewed before the gate
    // writes anything else on it, so it is only read until the report is attended
    const canClose = (DEV || isGuard)
        && !isClosed
        && !hasDiscrepancies;

    // The gate log is what the guard registers while the folio still waits for
    // the vehicle; once the arrival is registered, the materials are the ones
    // validated one by one before the movement can be closed.
    const isPendingArrival = item?.dispatchStatusId === STATUS_MODEL.PENDING_ARRIVAL;

    // A folio that was dispatched and still has to leave the plant is closed
    // with the departure the guard registers, so the log of the gate is read
    // above the materials that were already validated; the folio that already
    // left displays that same log, only read. The movement is read the same way
    // every form reads it, so the arrival the folio was closed with displays
    // its log too.
    // The report replaces the status the folio was dispatched with, so a folio
    // reported after the dispatch keeps reading the log the gate already wrote
    // instead of dropping it from the detail.
    const showDepartureLog = showsGuardGateMovementLog(item) || showReportedLog;

    // The gate writes nothing else on a folio that was reported, so its log is
    // read and no longer completed.
    const isLogDisabled = isClosed || hasDiscrepancies;

    // The log is written for the movement the folio waits for, so the notes ask
    // for what is seen when the vehicle comes in or for what is seen when it
    // leaves the plant
    const notePlaceholder = getGateLogNotePlaceholder({
        statusId: item?.dispatchStatusId,
        movementDirection: item?.movementDirection
    });

    return (
        <>
            <HeadTittle
                name={'details'}
                onBack={goBack}
                divStack={
                    <CustomChip legend={t(form.trans)}
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                variant={'compact'}
                                icon={form.Icon}
                                classNameIcon={'icon-color-secondary-700 h-16px w-16px'}
                    />
                }
            >
                {canClose &&
                    <>
                        <ButtonAction
                            id='btn_ggeed_dispatch'
                            className='btn-icon-edit'
                            Icon={EditIcon}
                            name='dispatch'
                            onClick={() => handleDispatch()}
                        />
                        <ButtonAction
                            id='btn_ggeed_report'
                            className='btn-icon-delete'
                            Icon={IconDocumentWarning}
                            name='report'
                            onClick={() => openReportModal()}
                        />
                    </>
                }
            </HeadTittle>
            <DivContainerBody>
                <div className={"mb-4"}>
                    <StatusBadgeSection
                        t={t}
                        item={item}
                        showBadge={showBadge}
                    />
                </div>
                <div className='row px-0'>
                    <div className='col-sm-12 col-md-4 col-lg-4 mb-lg-0 mb-md-3 mb-sm-3 pl-0 pr-sm-0 pr-md-3'
                         ref={containerCardRef}>
                        <EntryAndExitFormDetailsSectionInfo
                            item={item}
                            evidence={evidence}
                            form={form}
                            contentCardRef={contentCardRef}
                            showDepartureRecord={false}
                        />
                    </div>
                    <div className='col-sm-12 col-md-8 col-lg-8 px-0' ref={containerTableRef}>
                        {/* The detail stacks the sections the guard closes the movement with, so
                            the column is the one that scrolls and every card is displayed whole. */}
                        <div className={"overflow-y-auto"} ref={contentTableRef}>
                            {hasDiscrepancies &&
                                <div className={"mb-3"}>
                                    <DiscrepancySection
                                        t={t}
                                        reviewedBy={item?.dispatchUser}
                                        reviewedAt={item?.dispatchDate}
                                        reason={item?.dispatchReason}
                                        files={item?.discrepancyFiles}
                                    />
                                </div>
                            }

                            {isPendingArrival ?
                                <GuardGateLogSection
                                    t={t}
                                    gateLog={gateLog}
                                    handleChange={handleChange}
                                    photoPicker={photoPicker}
                                    setPhotoPicker={setPhotoPicker}
                                    emptyFields={emptyFields}
                                    disabled={isLogDisabled}
                                    notePlaceholder={notePlaceholder}
                                />
                                :
                                <>
                                    {showDepartureLog &&
                                        <div className={"mb-3"}>
                                            <GuardGateLogSection
                                                t={t}
                                                gateLog={gateLog}
                                                handleChange={handleChange}
                                                photoPicker={photoPicker}
                                                setPhotoPicker={setPhotoPicker}
                                                emptyFields={emptyFields}
                                                disabled={isLogDisabled}
                                                notePlaceholder={notePlaceholder}
                                            />
                                        </div>
                                    }

                                    {/* A folio that already dispatched its
                                    materials has nothing left to validate at
                                    the gate, so its materials are only read. */}
                                    {isDispatched &&
                                        <GateLogCard t={t} Icon={CubeIcon} title={'materials'}>
                                            <div className={"p-3"}>
                                                <MaterialsSection
                                                    t={t}
                                                    items={item?.items || []}
                                                    mode={MATERIALS_MODE.CARD}
                                                    dispatched={isDispatched}
                                                />
                                            </div>
                                        </GateLogCard>
                                    }

                                    {/* A reported folio keeps the list it was
                                    reported from, only read, with the evidence
                                    the guard already uploaded. */}
                                    {showMaterialsToValidate &&
                                        <MaterialDispatchListSection
                                            data={item}
                                            setModified={setModified}
                                            emptyFields={emptyFields}
                                            disabled={hasDiscrepancies}
                                            showDraftEvidence={true}
                                        />
                                    }
                                </>
                            }
                        </div>
                    </div>
                </div>
            </DivContainerBody>

            { showConfirmModal &&
                <ReportDispatchModal
                    show={showConfirmModal}
                    onHide={closeModal}
                    onConfirm={(comment, files) => handleActionModal(comment, files)}
                    title_modal={'report_dispatch'}
                    description={t('are_you_sure_to_proceed')}
                    subDescription={t('please_provide_the_required_information_below')}
                />
            }
        </>
    );
};

export default GuardGeneralEntryExitDetails;
